import { Eye, Target, Rocket, Gauge, ShieldCheck, Heart, Linkedin, Twitter } from "lucide-react";
import { Reveal, Stagger, StaggerItem, SectionLabel, Highlight } from "@/components/motion";
import CTABanner from "@/components/CTABanner";
import { TEAM, VALUES, MILESTONES } from "@/data/content";

const APPROACH = [
  { icon: Rocket, title: "Innovation", desc: "We prototype fast and adopt what's next before it's obvious." },
  { icon: Gauge, title: "Scalability & Performance", desc: "Systems designed for your tenth year, not just your first launch." },
  { icon: ShieldCheck, title: "Security by Default", desc: "Hardened architecture and best practices baked into every build." },
  { icon: Heart, title: "UX-First Philosophy", desc: <>If it isn't <Highlight type="underline" color="#FEF08A" delay={0.45}>intuitive and delightful</Highlight>, it isn't finished.</> },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 sm:px-8 overflow-hidden" data-testid="about-hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CAF2F9]/30 to-white" />
        <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-[#91E0F1]/20 blur-3xl" />
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionLabel>About Us</SectionLabel>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#01193D] leading-[1.1]">
              We believe technology should <Highlight delay={0.4}>empower</Highlight>, not just support
            </h1>
            <p className="text-[#475569] text-base lg:text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
              The Aurge Labs was founded on a simple conviction: most businesses aren't limited by ambition — they're limited by the tools they've been given.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="px-5 sm:px-8 py-16 lg:py-24" data-testid="brand-story-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden duotone">
              <img
                src="https://images.pexels.com/photos/7698712/pexels-photo-7698712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="The Aurge Labs team in a strategy session"
                className="w-full h-[420px] object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <SectionLabel>Our Story</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#01193D] leading-tight">
              From a small lab of ideas to a full-stack transformation partner
            </h2>
            <div className="space-y-5 text-[#475569] text-base leading-relaxed mt-6">
              <p>
                We started as a handful of engineers and designers frustrated by the same pattern: businesses buying software that forced them to work around it, instead of technology built to work for them.
              </p>
              <p>
                Today, The Aurge Labs is a multidisciplinary lab combining AI, software engineering, design, and growth strategy. We've delivered <Highlight delay={0.5}>150+ projects</Highlight> across a dozen industries — but the philosophy hasn't changed. Every system we ship should make our clients faster, sharper, and harder to compete with.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="px-5 sm:px-8 py-16 lg:py-20 bg-[#F8FAFC]" data-testid="vision-mission-section">
        <Stagger className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          <StaggerItem className="card-lift bg-[#01193D] rounded-3xl p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#2563EB]/20 blur-2xl" />
            <span className="inline-flex w-[52px] h-[52px] rounded-2xl bg-white/10 items-center justify-center">
              <Eye size={24} className="text-[#09B2ED]" />
            </span>
            <h3 className="font-display font-semibold text-2xl text-white mt-6">Our Vision</h3>
            <p className="text-slate-300 leading-relaxed mt-4">
              A world where every business — regardless of size — has access to the same calibre of intelligent technology as the giants, and the partnership to wield it well.
            </p>
          </StaggerItem>
          <StaggerItem className="card-lift bg-white rounded-3xl p-10 border border-slate-100 soft-shadow relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#CAF2F9] blur-2xl" />
            <span className="inline-flex w-[52px] h-[52px] rounded-2xl bg-gradient-to-br from-[#CAF2F9] to-white border border-[#91E0F1]/50 items-center justify-center">
              <Target size={24} className="text-[#2563EB]" />
            </span>
            <h3 className="font-display font-semibold text-2xl text-[#01193D] mt-6">Our Mission</h3>
            <p className="text-[#475569] leading-relaxed mt-4">
              To engineer digital products and intelligent systems that don't just support operations — they transform them, measurably and sustainably, one partnership at a time.
            </p>
          </StaggerItem>
        </Stagger>
      </section>

      {/* APPROACH */}
      <section className="px-5 sm:px-8 py-16 lg:py-24" data-testid="approach-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl">
            <SectionLabel>Our Approach</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D] leading-tight">
              Principles we never compromise on
            </h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 gap-6 mt-14">
            {APPROACH.map((a) => (
              <StaggerItem key={a.title} className="card-lift flex gap-5 bg-white rounded-3xl border border-slate-100 p-7 soft-shadow">
                <span className="shrink-0 inline-flex w-[52px] h-[52px] rounded-2xl bg-gradient-to-br from-[#CAF2F9] to-white border border-[#91E0F1]/50 items-center justify-center">
                  <a.icon size={24} className="text-[#2563EB]" />
                </span>
                <div>
                  <h3 className="font-display font-semibold text-lg text-[#01193D]">{a.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed mt-2">{a.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* TEAM */}
      <section className="px-5 sm:px-8 py-16 lg:py-24 bg-[#CAF2F9]/40" data-testid="team-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel>The Team</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D]">
              The minds behind the lab
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-14">
            {TEAM.map((m) => (
              <StaggerItem key={m.name}>
                <div data-testid={`team-card-${m.initials.toLowerCase()}`} className="card-lift group bg-white rounded-3xl border border-slate-100 p-8 text-center soft-shadow">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#2563EB] to-[#09B2ED] flex items-center justify-center">
                    <span className="font-display font-bold text-2xl text-white">{m.initials}</span>
                  </div>
                  <h3 className="font-display font-semibold text-lg text-[#01193D] mt-5">{m.name}</h3>
                  <p className="text-sm text-[#475569] mt-1">{m.role}</p>
                  <div className="flex justify-center gap-2 mt-4 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    <a href="#linkedin" aria-label={`${m.name} LinkedIn`} className="w-9 h-9 rounded-xl bg-[#CAF2F9] flex items-center justify-center text-[#2563EB] hover:bg-[#2563EB] hover:text-white transition-colors">
                      <Linkedin size={15} />
                    </a>
                    <a href="#twitter" aria-label={`${m.name} Twitter`} className="w-9 h-9 rounded-xl bg-[#CAF2F9] flex items-center justify-center text-[#2563EB] hover:bg-[#2563EB] hover:text-white transition-colors">
                      <Twitter size={15} />
                    </a>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* VALUES */}
      <section className="px-5 sm:px-8 py-16 lg:py-24" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel>Our Values</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D]">
              What we stand for
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-14">
            {VALUES.map((v, i) => (
              <StaggerItem key={v.title} className={`card-lift rounded-3xl p-7 border ${i === 0 ? "bg-[#01193D] border-transparent" : "bg-white border-slate-100 soft-shadow"}`}>
                <span className={`font-display font-bold text-3xl ${i === 0 ? "text-[#09B2ED]" : "text-gradient"}`}>0{i + 1}</span>
                <h3 className={`font-display font-semibold text-lg mt-4 ${i === 0 ? "text-white" : "text-[#01193D]"}`}>{v.title}</h3>
                <p className={`text-sm leading-relaxed mt-2 ${i === 0 ? "text-slate-300" : "text-[#475569]"}`}>{v.desc}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="px-5 sm:px-8 py-16 lg:py-24 bg-[#01193D] relative overflow-hidden" data-testid="milestones-section">
        <div className="absolute inset-0 dot-grid-light opacity-30" />
        <div className="absolute -top-32 right-1/4 w-96 h-96 rounded-full bg-[#2563EB]/20 blur-3xl animate-blob" />
        <div className="max-w-7xl mx-auto relative">
          <Reveal className="text-center max-w-2xl mx-auto">
            <SectionLabel dark>Milestones</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-white">
              The journey so far
            </h2>
          </Reveal>
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 mt-14" stagger={0.1}>
            {MILESTONES.map((m, i) => (
              <StaggerItem key={m.year}>
                <div data-testid={`milestone-${m.year}`} className="relative bg-white/[0.06] border border-white/10 rounded-3xl p-6 h-full hover:bg-white/10 transition-colors">
                  <span className="font-mono-brand text-xs font-semibold text-[#09B2ED]">{m.year} →</span>
                  <h3 className="font-display font-semibold text-white mt-3 leading-snug">{m.title}</h3>
                  <p className="text-slate-300 text-xs leading-relaxed mt-2">{m.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABanner title="Want to work with a team that thinks like partners?" subtitle="Let's talk about where your business could go next." />
    </div>
  );
}
